/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faFileExport } from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useConfig, { useTenantFormOptions } from "../../../../actions/Tenants/config/configHook";
import { UserRole } from "../../../../actions/Tenants/config/constantsTyped";
import { useLayerChecks } from "../../../../hooks/useLayerChecks";
import { RootState } from "../../../../reducers";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import useAuthorization from "../../../../utils/authorization";
import { Button } from "../../../Helper/Button";
import { InvalidLayerModal } from "../../AddItem/components/InvalidLayerModal";
import AllocateLayerModal from "./AllocateLayerModal";
import CreateLayerRelationModal from "./CreateLayerRelationModal";
import { EditLayerButton } from "./EditLayer";
import { ReportTemplate } from "./Report/utils";
import SendPalletsStatusToTenantModal from "./SendPalletsStatusToTenantModal";

import { useRouteLayer, useRouteLayerChildren } from "../../../../hooks/useCurrentEntities";

export const getLatestCheck = (checks, location_type: string[] | false = false, layer_id: string | null = null) => {
    const theChecks = checks
        .filter((i) => !isFilledArray(location_type) || getFilledArrayOrDefault(location_type).includes(i.location))
        .filter((i) => !layer_id || (i.layer_id === layer_id))
        .sort((a, b) => a.check_id - b.check_id);
    return theChecks[theChecks.length - 1];
};

export function LayerMeta() {
    const layer = useRouteLayer();
    const { data: checks = [] } = useLayerChecks(layer?.tenant_id, layer?.id);

    const latest_check = getLatestCheck(checks);
    const children = useRouteLayerChildren();
    const auth = useAuthorization();
    const user = useSelector<RootState, any>((state) => state.auth.user);
    const form_options = useTenantFormOptions();
    const config = useConfig();

    if (!layer || !layer.id) {
        return null;
    }

    const layer_type = config.get_layer_config(layer);

    if (!layer_type) {
        return null;
    }

    const reports = config.get_layer_pdf_reports(layer);

    return (
        <div>
            <div css={css`min-height: 20rem;`}>
                {layer_type.meta_display.map((i, index) => (
                    <MetaRow key={index} label={i.label} value={i.set_value({
                        config,
                        layer,
                        check: latest_check, // * Make sure to copy the fields you need with copy_check_to_layer_meta
                        fruit: null, // * Fruit list is not available on overview screen
                        children,
                        form_options // * used to translate form values to labels
                    }).display_component()} />
                ))}

                <div className="d-flex flex-wrap pt-4 gap-2 justify-content-end">
                    <CreateLayerRelationModal />

                    {(user.is_admin || auth.userHasRole(UserRole.TENANT_ADMIN)) && <InvalidLayerModal layer={layer} />}

                    <EditLayerButton layer_type={layer_type} />

                    {layer_type.show_allocate_button && <AllocateLayerModal />}
                </div>

                {(reports.length > 0 || layer_type.show_send_status_to_tenant_button) && (
                    <div className="w-100 d-flex flex-wrap gap-2 justify-content-center pt-3 mt-5 border-top">
                        {layer_type.show_send_status_to_tenant_button && <SendPalletsStatusToTenantModal />}

                        {reports.map((i, index) => {
                            if (i.template !== ReportTemplate.latest_check) {
                                return (
                                    <Button
                                        key={index}
                                        tag={Link}
                                        to={`/layer/${layer.id}/report/${i.value}`}
                                        color="primary"
                                        className="text-capitalize flex-grow-1"
                                        icon={faFileExport}
                                    >
                                        {i.text} report
                                    </Button>
                                );
                            }

                            const latestCheck = getLatestCheck(checks, getFilledArrayOrDefault(i.check_types, [i.value]));

                            if (latestCheck) {
                                return (
                                    <Button
                                        key={index}
                                        tag={Link}
                                        to={`/layer/${layer.id}/add-check/${latestCheck.check_id}/report`}
                                        color="primary"
                                        className="text-capitalize flex-grow-1"
                                        icon={faFileExport}
                                    >
                                        {i.text} Report
                                    </Button>
                                );
                            }

                            return null;
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

interface LayerMetaProps {
    label: React.ReactNode;
    value: React.ReactNode;
}

export function MetaRow({ label, value }: LayerMetaProps) {
    return (
        <div className="d-sm-flex align-items-center py-1">
            <div className="text-muted pe-2" css={css`width: 15rem;`}>
                {label}
            </div>

            <div className="ms-auto text-end text-capitalize">
                {typeof value === "string" ? value.split(",").join(", ") : value}
            </div>
        </div>
    );
}
